import { LIFE_TEST_ID, POL_TYPE } from './life-check';

export enum OPERATION_TYPE {
	HOME = 'HOME',
	OCR = 'OCR',
	OCR_CANNY = 'OCR_CANNY',
	POL = 'POL',
	DOCUMENTS = 'DOCUMENTS',
	CONNECTIONS = 'CONNECTIONS',
	FORM = 'FORM',
	FINISH = 'FINISH',
	CHOOSE = 'CHOOSE',
}

export enum DOCS_TYPE {
	DRIVE_LICENSE = 'DRIVE_LICENSE',
	DNI = 'DNI',
	PASSPORT = 'PASSPORT',
	NIE = 'NIE',
	CERT_REG_CIU_UE = 'CERT_REG_CIU_UE',
	IRPF = 'IRPF',
	CHECK = 'CHECK',
	INVOICE = 'INVOICE',
	WORKLIFE = 'WORKLIFE',
	PAYROLL = 'PAYROLL',
	SCRIPTURE = 'SCRIPTURE',
	INCORPORATION = 'INCORPORATION',
	GENERIC = 'GENERIC',
}

export enum CONNECTION_TYPE {
	BANK_SCORING = 'BANK_SCORING',
	SCRIPTURE = 'SCRIPTURE',
	IRPF = 'IRPF',
	WORK_LIFE = 'WORKLIFE',
}

export enum MODE {
	AUTO = 'auto',
	MANUAL = 'manual',
	/** Captura activa por defecto + adjuntar / sacar foto (+ MiDNI si aplica). NIF-802. */
	FLEXIBLE = 'flexible',
}

export enum MODEL_DOCUMENT_CATEGORY {
	/** Categoría genérica del modelo MediaPipe (DNI/NIE reverso y anverso). */
	DOCUMENT = 'document',
	PASSPORT = 'passport',
	FACE = 'face',
}

export type IOperation =
	| IHomeOperation
	| IOcrOperation
	| IOcrCannyOperation
	| IPolOperation
	| IDocumentsOperation
	| IFormOperation
	| IFinishOperation
	| IChooseOperation
	| IConnectionOperation;

export enum OPERATION_STATUS {
	OK = 'OK',
	KO = 'KO',
	CANCELLED = 'CANCELLED',
	INIT = 'INIT',
}

export interface IMessages {
	[TestStatus.OnInit]: string; // Estado inicial
	[TestStatus.OnComplete]: string; // Estado completado
	[TestStatus.OnWarning]: string; // Estado repetición
	[TestStatus.OnFail]: string; // Estado fallido
	[TestStatus.OnWarningMismatch]?: string; // Estado warning descentrado
}
export interface IAudios {
	[TestStatus.OnInit]: string; // Estado inicial
	[TestStatus.OnComplete]: string; // Estado completado
	[TestStatus.OnWarning]: string; // Estado repetición
	[TestStatus.OnFail]: string; // Estado fallido
	[TestStatus.OnWarningMismatch]?: string; // Estado warning descentrado
}

export enum TestStatus {
	OnInit = 'onInit',
	OnComplete = 'onComplete',
	OnWarning = 'onWarning',
	OnFail = 'onFail',
	OnWarningMismatch = 'onWarningMismatch',
}

export interface ITestTries {
	count?: number;
	onWarning: number;
}

export interface ITestTimeConf {
	max: number; // seg
	min: number; // seg
}

export const quality: number = 0.95;

export interface IPoint {
	x: number;
	y: number;
	height: number;
	width: number;
}

// Warning pages
export interface IPageWarning {
	show: boolean;
	type: PAGE_WARNING;
	/** Override i18n del título (p. ej. SCRIPTURE PDF 2053+). */
	titleKey?: string;
	/** Override i18n del subtítulo. */
	subtitleKey?: string;
}

export enum PAGE_WARNING {
	HELP = 'help',
	NO_FACE = 'noFace',
	MULTIPLE_FACES = 'multipleFaces',
	VERIFICATION_FAIL = 'verificationFail',
	SERVER_ERROR = 'serverError',
	CAMERA_ERROR = 'cameraError',
	INACTIVITY_TIMEOUT = 'inactivityTimeout',
	BACK_TO_OCR = 'backToOcr',
	INSUFFICIENT_OCR_READ = 'insufficientOcrRead',
	/** NIF-921: falta cara al Continuar en upload Flexible (pantalla completa). */
	FLEXIBLE_UPLOAD_MISSING_SIDE = 'flexibleUploadMissingSide',
	/** NIF-988: falta cara / lado incorrecto (no bucket MRZ). */
	INCORRECT_DOCUMENT_SIDE = 'incorrectDocumentSide',
	/** Anverso/reverso de documentos distintos (NIF-984). */
	DIFFERENT_DOCUMENTS = 'differentDocuments',
	/** Anverso y reverso en la misma foto al adjuntar (NIF-1082). */
	BOTH_SIDES_IN_ONE_IMAGE = 'bothSidesInOneImage',
	/** Faltan datos de dirección (OCR 2038; NIF-1085). */
	MISSING_ADDRESS = 'missingAddress',
	/** Documento sin rostro detectable (OCR 2032; NIF-1109). */
	NO_FACE_IN_DOCUMENT = 'noFaceInDocument',
}

export type TProcessLogs = ProcessLog.PRELOAD | ProcessLog.IN_PROCESS | ProcessLog.PROCESS_STATUS | ProcessLog.FINISH;
export type TPolLogs = PolLog.PRELOAD | PolLog.IN_PROCESS | PolLog.FINISH | PolLog.SCREENSHOT;
export type TPolManualLogs =
	| PolManualLog.PRELOAD
	| PolManualLog.IN_PROCESS
	| PolManualLog.FINISH
	| PolManualLog.SCREENSHOT;
export type TOcrLogs = OcrLog.PRELOAD | OcrLog.IN_PROCESS | OcrLog.REQUEST | OcrLog.FINISH | OcrLog.SCREENSHOT;
export type TDocsLogs = DocsLog.PRELOAD | DocsLog.IN_PROCESS | DocsLog.FINISH | DocsLog.SCREENSHOT;
export type TConnectionLogs = ConnectionLog.FINISH;
export type THomeLogs = HomeLog.POLICY_CHECKS_ERROR;
/** NIF-1034: fallos quirúrgicos de Howler / Web Audio (visibles en Cloud Logs). */
export type TAudioLogs = AudioLog.RESUME_FAIL | AudioLog.PLAY_ERROR | AudioLog.LOAD_ERROR;
export type TConsoleLogs =
	| ConsoleLog.ERROR
	| ConsoleLog.WARNING
	| ConsoleLog.UNHANDLED_ERROR
	| ConsoleLog.UNHANDLED_REJECTION
	| ConsoleLog.BATCH
	| ConsoleLog.DEVTOOLS_OPENED;

export enum ProcessLog {
	PRELOAD = 'Process - Preload',
	IN_PROCESS = 'Process - In process',
	PROCESS_STATUS = 'Process - getProcessStatus',
	FINISH = 'Process - Finish',
}

export enum PolLog {
	PRELOAD = 'PoL - Preload',
	IN_PROCESS = 'PoL - In process',
	FINISH = 'PoL - Finish',
	SCREENSHOT = 'screenShot',
}

export enum PolManualLog {
	PRELOAD = 'PoL Manual - Preload',
	IN_PROCESS = 'PoL Manual - In process',
	FINISH = 'PoL Manual - Finish',
	SCREENSHOT = 'PoL Manual - screenShot',
}

export enum OcrLog {
	PRELOAD = 'Ocr - Preload',
	IN_PROCESS = 'Ocr - In process',
	REQUEST = 'Ocr - Request',
	FINISH = 'Ocr - Finish',
	SCREENSHOT = 'screenShot',
}

export enum DocsLog {
	PRELOAD = 'Docs - Preload',
	IN_PROCESS = 'Docs - In process',
	FINISH = 'Docs - Finish',
	SCREENSHOT = 'screenShot',
}

export enum ConnectionLog {
	FINISH = 'Connection - Finish',
}

export enum HomeLog {
	POLICY_CHECKS_ERROR = 'Home - Policy checks error',
}

/** Errores y avisos capturados automáticamente del navegador (consola / unhandled). */
export enum ConsoleLog {
	ERROR = 'Console - error',
	WARNING = 'Console - warning',
	UNHANDLED_ERROR = 'Console - unhandled error',
	UNHANDLED_REJECTION = 'Console - unhandledrejection',
	BATCH = 'Console - batch',
	DEVTOOLS_OPENED = 'Console - DevTools opened',
}

/** NIF-1034: telemetría quirúrgica de audio (Howler / AudioContext). */
export enum AudioLog {
	RESUME_FAIL = 'Audio - resume fail',
	PLAY_ERROR = 'Audio - play error',
	LOAD_ERROR = 'Audio - load error',
}

export enum TEST_STATUS {
	OK = 'ok',
	KO = 'ko',
	KO_MAXTRIES = 'ko-maxtries',
	KO_CAMERA = 'ko-camera',
	KO_PERMISSION = 'ko-permission',
	KO_HELP = 'ko-help',
	KO_NO_FACE = 'ko-noFace',
	KO_PREVIEW_UNUSABLE = 'ko-previewUnusable',
	KO_MULTIPLE_FACES = 'ko-multipleFaces',
	KO_HANDSET_LANDSCAPE = 'ko-handsetLandscape',
	KO_TUTORIAL = 'ko-tutorial',
	RETRY = 'retry',
}

export enum PROCESS_STATUS {
	OK = 'OK',
	KO = 'KO',
	INPROGRESS = 'IN_PROGRESS',
	CANCELLED = 'CANCELLED',
	PENDING = 'PENDING',
	INIT = 'INIT',
	STANDBY = 'STANDBY',
}

/** `processes.details_code` cuando el cron caduca el proceso (estado CANCELLED). */
export const PROCESS_EXPIRED_DETAILS_CODE = 1098;
/** NIF-969: back rechaza `POST /operation/finish` con pasos pendientes sin KO previo. */
export const PREMATURE_FINISH_ERROR_CODE = 1120;

export enum EVALUATION_TYPE {
	MATCH_DATA = 'match_data',
	SCORING = 'scoring',
}

export type LocalizedText = string | Record<string, string>;

export interface IProcess {
	operations: Array<IOperation>;
	config?: IProcessConfig;
	design?: IProcessDesign;
	status_process: PROCESS_STATUS;
	status_device?: PROCESS_STATUS;
	device_id?: string;
	main_process?: boolean;
	company?: string;
	process?: string;
}

export interface IHomeOperation {
	type: OPERATION_TYPE.HOME;
	config: {
		tecalis_policy_check: boolean;
		policy_checks?: IPolicyCheck[];
		description?: LocalizedText;
		isLocationSaved?: boolean; // Control para el front
		iframe?: boolean;
	};
	state?: OPERATION_STATUS;
	completed: boolean;
	uuid: string;
}

export interface IPolicyCheck {
	custom_id: string;
	html: LocalizedText;
	required: boolean;
	uuid: string;
}
export interface IOcrOperation {
	type: OPERATION_TYPE.OCR;
	config: {
		iframe?: boolean;
		mode: MODE;
		tutorial: boolean;
		private_mode?: boolean; // Tapar datos de documento y nombre
		allowed_docs?: IOcrAllowedDocs;
		not_allowed_docs?: IOcrAllowedDocs;
		fraud_scoring: {
			aeat: boolean;
			legal_age: number;
			expirated: boolean;
			valid_country: boolean;
			aml: boolean;
			pep: boolean;
			manipulated_photo?: {
				blocking_step: boolean;
				ip_block: boolean;
				identifier_block: boolean;
				email_alert: string;
			};
			photocopy_check?: {
				blocking_step: boolean;
				ip_block: boolean;
				identifier_block: boolean;
				email_alert: string;
			};
			screen_photo?: {
				blocking_step: boolean;
				ip_block: boolean;
				identifier_block: boolean;
				email_alert: string;
			};
		};
		match_data?: string;
		required_check_nie?: boolean;
		setting?: IOcrSetting;
		fallback?: IFallbackOperation;
		/** Lado de la foto en el wireframe DNI enviado por la operación OCR (NIF-700). */
		dni_wireframe_photo_side?: 'left' | 'right' | null;
		/** Workflow ofusca el retrato (NIF-536); gate de `face_bbox`. */
		obfuscation_photo?: boolean;
		current_data?: any;
		midni_validation_enabled?: boolean;
		eudi_wallet_enabled?: boolean;
		/** NIF-960: omitir POL tras autenticación EUDI (default true en admin). */
		skip_pol_when_eudi_authenticated?: boolean;
		/** Omitir POL tras autenticación MiDNI. */
		skip_pol_when_midni_authenticated?: boolean;
	};
	state?: OPERATION_STATUS;
	completed: boolean;
	pending_error_code?: number | null;
	uuid: string;
}

export interface IOcrAllowedDocs {
	passport?: Array<string>;
	dni?: Array<string>;
	nie?: boolean;
}

export interface IOcrCannyOperation {
	type: OPERATION_TYPE.OCR_CANNY;
	config: {
		iframe?: boolean;
		mode: MODE;
		tutorial: boolean;
		private_mode?: boolean; // Tapar datos de documento y nombre
		allowed?: string[]; // DNI, NIE, PASAPORTE, LICENCIA DE CONDUCIR
		not_allowed?: string[]; // DNI, NIE, PASAPORTE, LICENCIA DE CONDUCIR
		fraud_scoring:
			| boolean
			| {
					aeat: boolean;
					legal_age: number;
					expirated: boolean;
					valid_country: boolean;
					manipulated_photo: boolean;
					photocopy_check: boolean;
			  };
		setting?: IOcrSetting;
		required_check_nie?: boolean;
		match_data?: string;
		fallback?: MODE.MANUAL;
	};
	state?: OPERATION_STATUS;
	completed: boolean;
	uuid?: string;
}

export interface IPolOperation {
	type: OPERATION_TYPE.POL;
	config: {
		iframe?: boolean;
		mode: MODE;
		tutorial?: boolean;
		setting?: IPolSetting;
		fraud_scoring:
			| boolean
			| {
					facial_recognition: boolean;
					face_match: boolean;
			  };
		max_time?: number; // seg
		fallback?: IFallbackOperation;
	};
	state?: OPERATION_STATUS;
	completed: boolean;
	uuid: string;
}
export interface IDocumentsOperation {
	type: OPERATION_TYPE.DOCUMENTS;
	config: {
		iframe?: boolean;
		docs?: Array<IDocumentsDocs>;
		title?: LocalizedText;
		tutorial?: boolean;
		allow_photo?: boolean;
	};
	state?: OPERATION_STATUS;
	completed: boolean;
	uuid: string;
}

export interface IChooseOperation {
	type: OPERATION_TYPE.CHOOSE;
	config: {
		description?: LocalizedText;
		operations: Array<IOperation>;
	};
	completed: boolean;
	uuid: string;
}

export interface IConnectionOperation {
	type: OPERATION_TYPE.CONNECTIONS;
	config: IWorkLifeConfig | IBankScoringConfig | IIrpfConfig | IScriptureConfig;
	state?: OPERATION_STATUS;
	completed: boolean;
	uuid: string;
}

export interface IWorkLifeConfig {
	type: CONNECTION_TYPE.WORK_LIFE;
	mode: MODE;
	fallback?: IFallbackOperation;
	connection_type_uuid: string;
	total_tries?: number;
	evaluation?: EVALUATION_TYPE;
	numberOfTries?: number; // Control para el front, número de intentos realizados
}

export interface IBankScoringConfig {
	type: CONNECTION_TYPE.BANK_SCORING;
	mode: MODE;
	match_name?: boolean;
	iban?: string;
	ruleset?: string;
	connection_type_uuid: string;
	total_tries?: number;
	evaluation?: EVALUATION_TYPE;
	numberOfTries?: number; // Control para el front, número de intentos realizados
}

export interface IIrpfConfig {
	type: CONNECTION_TYPE.IRPF;
	mode: MODE;
	fallback?: IFallbackOperation;
	connection_type_uuid: string;
	total_tries?: number;
	evaluation?: EVALUATION_TYPE;
	numberOfTries?: number; // Control para el front, número de intentos realizados
}

export interface IScriptureConfig {
	type: CONNECTION_TYPE.SCRIPTURE;
	mode: MODE;
	cif: string;
	fallback?: IFallbackOperation;
	connection_type_uuid: string;
	total_tries?: number;
	evaluation?: EVALUATION_TYPE;
	numberOfTries?: number; // Control para el front, número de intentos realizados
}
export interface IDocumentsDocs {
	uuid: string;
	document_type_uuid: string;
	type: string;
	required: boolean;
	total_tries?: number;
	method: 'photo' | 'manual' | 'both';
	min?: number;
	max?: number;
	title?: LocalizedText;
	description?: LocalizedText;
	instructions?: LocalizedText;
	instructions_photo?: LocalizedText;
	preview_validation?: boolean;
	match_data?: any; // TODO: Tipar
	evaluation?: EVALUATION_TYPE;
	post_poned_expiration?: number;
	ruleset?: any;
	allow_photo?: boolean;
	connection_type?: CONNECTION_TYPE;
}

export interface IPolSetting {
	total_tries?: number;
	type?: POL_TYPE;
	available_tests?: LIFE_TEST_ID[]; // 'smile', 'turn-right'
	total_test?: number;
	scoring?: number;
}

export interface IOcrSetting {
	total_tries: number;
	side_max_time: number;
	show_guide: boolean;
}

export interface IFormFieldConfig {
	uuid: string;
	id: string;
	name: string;
	type: 'text' | 'textarea' | 'select' | 'checkbox' | 'radio' | 'file';
	title: LocalizedText;
	description?: LocalizedText;
	col: number;
	validators?: string[];
	options?: Array<{ value: string; label: LocalizedText }>;
	default_value?: string;
	/** NIF-658: bloqueado tras autofill OCR (identidad). */
	disabled?: boolean;
	multiple?: boolean;
}

export interface IFormStepConfig {
	uuid: string;
	order: number;
	title: LocalizedText;
	description?: LocalizedText;
	alert_msg?: LocalizedText;
	fields: IFormFieldConfig[];
}

export interface IFormOperation {
	type: OPERATION_TYPE.FORM;
	config: {
		tutorial?: boolean;
		/** Preferencia Admin NIF-657; prefill runtime NIF-658. */
		autofill_from_ocr?: boolean | null;
		steps: IFormStepConfig[];
	};
	completed: boolean;
	uuid: string;
	state?: OPERATION_STATUS;
	current_step_order?: number;
}

export interface IFinishOperation {
	type: OPERATION_TYPE.FINISH;
	config: {
		status?: OPERATION_STATUS;
		iframe?: boolean;
		loading?: boolean;
		loading_text?: LocalizedText;
		ok_title?: LocalizedText;
		ok_description?: LocalizedText;
		ko_title?: LocalizedText;
		ko_description?: LocalizedText;
		cancelled_title?: LocalizedText;
		cancelled_description?: LocalizedText;
		pending_title?: LocalizedText;
		pending_description?: LocalizedText;
		callback?: string;
		callback_ko?: string;
		callback_cancelled?: string;
		redirect?: string;
		redirect_ko?: string;
		redirect_cancelled?: string;
	};
	completed: boolean;
	uuid: string;
}

export interface IProcessConfig {
	/** Código ISO del país de la compañía (ej. ESP), expuesto en GET /process/config. */
	company_country?: string;
	allowed_device?: Array<'mobile' | 'pc'>;
	default_lang?: string;
	has_saved_state?: boolean;
	location?: 'disabled' | 'enabled' | 'mandatory';
	media_expiry?: number;
	include_images?: boolean;
	metadata?: any;
	version?: string;
	isDeviceAllowed?: boolean; // FRONT:Indica si el dispositivo es permitido
	required_recording?: boolean;
	delayed_process_warning: boolean;
	delayed_process_warning_visibility?: 'start_and_resume' | 'resume_only' | null;
	delayed_process_warning_text: LocalizedText;
	sdk?: boolean;
	antifraud?: IProcessAntifraud;
	notify?: {
		email: {
			to: string;
			template: string;
			subject: string;
			from: string;
			from_name: string;
		};
		sms: {
			phone: string;
			sms_text: string;
			sms_from: string;
		};
	};
	validation: {
		type: MODE;
		emails: string;
	};
	/** Valores requeridos en autorización (p. ej. NIF para carta verde), expuestos por el backend en getConfig. */
	requireds?: {
		nif?: string;
		[key: string]: string | undefined;
	};
}

/** Entrada de personalización CSS devuelta por el backend en `design.css_config`. */
export interface ICssConfigItem {
	uuid: string;
	variable: string;
	description: string;
	variable_type: 'color' | 'number' | 'text';
	value: string;
}

export interface IProcessDesign {
	css_config?: ICssConfigItem[] | null;
	logo?: string;
	skin?: 'dark' | 'light';
	theme_color?: string; // Diferentes colores en el navegador del movil (revisar)
	title?: string; // Titulo de la pestaña
	watermark?: boolean;
	showWatermark?: boolean; // Control para el front, si se muestra o no la marca de agua
	/** Mostrar nombre de producto junto al logo (solo con logo Tecalis, sin logo personalizado). */
	show_product_name?: boolean;
	/** URL del spinner personalizado (SVG) definido en admin. */
	custom_spinner?: string | null;
	/** Favicon del tema (URL o base64); FormData `favicon` en design_configs. */
	favicon?: string | null;
}

export interface IDeviceRegisterNonceResponse {
	nonce: string;
}

export interface IDeviceRegisterResponse {
	device_id: string;
	primary_device: boolean;
	status_process?: string;
}

export interface IProcessConfigResponse {
	company: string;
	code: number;
	/** Respuesta `GET /process/config`: tipo de 2FA requerido; solo en la raíz. `false` cuando ya está superado. */
	'2fa'?: false | 'sms' | 'whatsapp';
	config: IProcessConfig;
	design?: IProcessDesign;
	device_id: string;
	main_process: boolean;
	operations: IOperation[];
	process: string;
	status_process: PROCESS_STATUS;
	status_process_device: PROCESS_STATUS;
	/** `processes.details_code`; 1098 = caducado por tiempo. */
	process_status_code?: number | null;
	pwcs_url: string;
	primary_device: boolean;
	/** false en bootstrap sin ProcessDevice (NIF-650); true tras register. */
	device_registered?: boolean;
	jwt: string;
	apiKey: string;
}

export interface IProcessAntifraud {
	email_alert: Array<string> | null;
	face_block: boolean;
	face_block_global: boolean;
	identifier_block: boolean;
	identifier_block_global: boolean;
	ip_block: boolean;
	ip_block_global: boolean;
	block_operation: boolean;
}

export interface IProcessStatusResponse {
	primary_device: boolean;
	status_process_device: PROCESS_STATUS;
	/** `processes.details_code`; 1098 = caducado por tiempo. */
	process_status_code?: number | null;
}

export interface IProcessCheckStatusResponse {
	token?: string;
}

export interface IProcessFinishResponse {
	uuid_operation: string;
	status: OPERATION_STATUS;
	/** true cuando el proceso queda en revisión manual (State::PENDING). */
	pending_review?: boolean;
	/** NIF-1026: motivo enseñable resuelto en back (omitido si no aplica). */
	ko_reason?: {
		error_code: number;
		title: string;
		message: string;
	} | null;
}

export const process_response: IProcess = {
	operations: [
		{
			type: OPERATION_TYPE.HOME,
			config: {
				tecalis_policy_check: true,
				policy_checks: [{ custom_id: 'check1', html: 'Politicas', required: true, uuid: 'prueba' }],
				description: 'Descripcion personalizada',
			},
			completed: false,
			uuid: 'uuid-home-12345',
		},
		{
			type: OPERATION_TYPE.OCR,
			config: {
				mode: MODE.AUTO,
				tutorial: true,
				private_mode: false,
				fraud_scoring: {
					aeat: false,
					legal_age: 20,
					expirated: true,
					valid_country: true,
					aml: false,
					pep: false,
					manipulated_photo: {
						blocking_step: false,
						ip_block: false,
						identifier_block: false,
						email_alert: 'rafael.cruz@tecalis.com',
					},
					photocopy_check: {
						blocking_step: false,
						ip_block: false,
						identifier_block: false,
						email_alert: 'rafael.cruz@tecalis.com',
					},
					screen_photo: {
						blocking_step: false,
						ip_block: false,
						identifier_block: false,
						email_alert: 'rafael.cruz@tecalis.com',
					},
				},
				match_data: '75116102B',
				required_check_nie: false,
				setting: null,
			},
			completed: false,
			uuid: 'uuid-ocr-12345',
		},
		{
			type: OPERATION_TYPE.POL,
			config: {
				mode: MODE.AUTO,
				tutorial: true,
				setting: {
					total_tries: 3,
					type: POL_TYPE.RANDOM,
					total_test: 3,
				},
				fraud_scoring: true,
			},
			completed: false,
			uuid: 'uuid-pol-12345',
		},
		{
			type: OPERATION_TYPE.FINISH,
			config: {
				loading: true,
				loading_text: 'Proceso finalizado',
				redirect: 'https://www.google.com',
				redirect_ko: 'https://www.google.com/ko',
			},
			completed: false,
			uuid: 'uuid-finish-12345',
		},
	],
	config: {
		allowed_device: ['mobile', 'pc'],
		version: '3',
		metadata: 'ejemplo',
		location: 'enabled',
		media_expiry: 300,
		has_saved_state: true,
		delayed_process_warning: false,
		delayed_process_warning_visibility: null,
		delayed_process_warning_text: '',
		validation: {
			type: MODE.AUTO,
			emails: '',
		},
	},
	design: {
		skin: 'light',
		logo: '',
		title: '',
		css_config: [],
		// theme_color: '#000000',
		watermark: true,
	},
	status_process: PROCESS_STATUS.INIT,
	status_device: PROCESS_STATUS.INIT,
	device_id: '1234567890',
	main_process: true,
	company: 'Tecalis SL',
};

export interface IFallbackOperation {
	emails?: string;
	type?: MODE.MANUAL;
	validation?: MODE;
}
